import { Box, Divider, Typography, useTheme } from '@mui/material'
import {
  GroupCardAvatar,
  GroupCardAvatarContainer
} from 'components/cards/styled'
import PopupSection from 'components/popup/PopupSection'
import i18n from 'i18n'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { GroupDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { defaultGroupImage } from 'utils/api/groups'

import { UserMenuPopup } from '../styled'

export interface StorageByGroupItem {
  groupId: string
  group?: Model<GroupDoc>
  groupName: string
  groupColor?: string
  photosUsed: number
}

interface StorageByGroupPopupProps {
  open: boolean
  setOpen: (b: boolean) => void
  children: ReactNode
  items: StorageByGroupItem[]
  totalPhotos: number
  photoQuotaLimit: number
  accentColor: string
}

const GROUP_ROW_HEIGHT_PX = 40
const MAX_VISIBLE_GROUP_ROWS = 5.5

function Content(props: StorageByGroupPopupProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const quotaValue =
    props.photoQuotaLimit > 0 ? String(props.photoQuotaLimit) : t('unlimited')
  const listMaxHeight = GROUP_ROW_HEIGHT_PX * MAX_VISIBLE_GROUP_ROWS

  return (
    <Box sx={{ width: '350px', maxWidth: '82vw' }}>
      <PopupSection
        title={t('photoAttachmentsPerCalendar')}
        noBottomBorder
        titlePadding={
          i18n.dir() === 'ltr'
            ? '0.8rem 0.5rem 0rem 1rem'
            : '0.8rem 1rem 0rem 0.5rem'
        }
        padding={'0.4rem 0rem 0.5rem 0rem'}
      >
        <Box sx={{ px: 1.6 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 0.8,
              pt: '1rem'
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'uppercase',
                color: theme.palette.text.secondary
              }}
            >
              {t('calendar')}
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'uppercase',
                color: theme.palette.text.secondary
              }}
            >
              {t('photosUsed')}
            </Typography>
          </Box>

          <Box
            sx={{
              maxHeight: `${listMaxHeight}px`,
              overflowY: 'auto',
              pr: 0.25,
              scrollbarGutter: 'stable'
            }}
          >
            {props.items.map((item) => {
              const avatarBorderColor =
                item.groupColor ?? theme.palette.border.primary ?? '#c8c8c8'

              return (
                <Box
                  key={item.groupId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: `${GROUP_ROW_HEIGHT_PX}px`,
                    gap: 1,
                    pr: '0.85rem'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                      gap: '0.6rem',
                      flexGrow: 1
                    }}
                  >
                    <GroupCardAvatarContainer
                      color={avatarBorderColor}
                      width={34}
                      height={34}
                      borderWidth={'2px'}
                    >
                      <GroupCardAvatar
                        asGroup
                        size={26}
                        name={item.groupName}
                        img={item.group?.Photo}
                        emptyPic={defaultGroupImage(item.group)}
                      />
                    </GroupCardAvatarContainer>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: 400,
                        color: theme.palette.text.primary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {item.groupName}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      minWidth: '26px',
                      textAlign: 'right',
                      fontSize: '14px',
                      lineHeight: '20px',
                      fontWeight: 400,
                      color: theme.palette.text.secondary
                    }}
                  >
                    {item.photosUsed}
                  </Typography>
                </Box>
              )
            })}
          </Box>

          <Divider sx={{ borderColor: theme.palette.border.primary }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              pt: 1
            }}
          >
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: theme.palette.text.primary
              }}
            >
              {t('totalPhotosUsed')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.4,
                direction: 'ltr'
              }}
            >
              <Typography
                sx={{
                  fontSize: '24px',
                  lineHeight: 1,
                  fontWeight: 600,
                  color: props.accentColor
                }}
              >
                {props.totalPhotos}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.1,
                  fontWeight: 400,
                  color: theme.palette.text.secondary
                }}
              >
                /{quotaValue}
              </Typography>
            </Box>
          </Box>
        </Box>
      </PopupSection>
    </Box>
  )
}

export default function StorageByGroupPopup(props: StorageByGroupPopupProps) {
  return (
    <UserMenuPopup
      open={props.open}
      setOpen={props.setOpen}
      placement={i18n.dir() === 'ltr' ? 'right' : 'left'}
      fallbackPlacements={
        i18n.dir() === 'ltr' ? ['right', 'left', 'top'] : ['left', 'top']
      }
      content={<Content {...props} />}
    >
      {props.children}
    </UserMenuPopup>
  )
}
